import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Building2, CheckCircle2, ClipboardCheck, FileText, Save } from "lucide-react";
import { apiRequest } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const statusLabels = {
  non_iniziato: "Non iniziato",
  incompleto: "Incompleto",
  completo: "Completo",
};

const statusClasses = {
  non_iniziato: "bg-slate-100 text-slate-600",
  incompleto: "bg-amber-50 text-amber-700",
  completo: "bg-emerald-50 text-emerald-700",
};

function CompanyPicker() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRequest("/companies").then(setCompanies).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="dvr-wizard-picker-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-wizard-picker-header">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900" data-testid="dvr-wizard-picker-kicker">Wizard DVR</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="dvr-wizard-picker-title">Seleziona azienda</h2>
        <p className="mt-2 text-slate-600" data-testid="dvr-wizard-picker-description">Apri il percorso guidato per aggiornare assessment, step e completamento DVR.</p>
      </section>
      <Card className="rounded-lg border-slate-200 shadow-none" data-testid="dvr-wizard-company-list-card">
        <CardContent className="space-y-3 p-6">
          {loading && <p data-testid="dvr-wizard-picker-loading">Caricamento aziende...</p>}
          {!loading && companies.length === 0 && <p className="rounded-md border border-dashed border-slate-300 p-6 text-sm text-slate-500" data-testid="dvr-wizard-picker-empty">Nessuna azienda disponibile.</p>}
          {companies.map((company) => (
            <Link key={company.id} to={`/aziende/${company.id}/dvr-wizard`} className="flex flex-col justify-between gap-3 rounded-md border border-slate-200 p-4 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-slate-50 sm:flex-row sm:items-center" data-testid={`dvr-wizard-company-${company.id}`}>
              <div>
                <p className="font-heading text-lg font-semibold text-slate-950" data-testid={`dvr-wizard-company-name-${company.id}`}>{company.business_name}</p>
                <p className="text-sm text-slate-500" data-testid={`dvr-wizard-company-meta-${company.id}`}>P.IVA {company.vat_number || "—"} · {company.city || "—"}</p>
              </div>
              <div className="min-w-48" data-testid={`dvr-wizard-company-progress-wrap-${company.id}`}>
                <div className="mb-2 flex justify-between text-sm"><span>Base DVR</span><strong>{company.completion_percentage}%</strong></div>
                <Progress value={company.completion_percentage} data-testid={`dvr-wizard-company-progress-${company.id}`} />
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function StepChecklist({ steps, activeKey, setActiveKey }) {
  return (
    <aside className="space-y-2" data-testid="dvr-wizard-step-checklist">
      {steps.map((step, index) => (
        <button
          key={step.step_key}
          onClick={() => setActiveKey(step.step_key)}
          className={`w-full rounded-md border p-3 text-left transition-[background-color,border-color,transform] hover:-translate-y-0.5 ${activeKey === step.step_key ? "border-blue-900 bg-blue-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
          data-testid={`dvr-step-checklist-${step.step_key}`}
        >
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-950 text-xs font-bold text-white" data-testid={`dvr-step-index-${step.step_key}`}>{index + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-950" data-testid={`dvr-step-title-${step.step_key}`}>{step.step_title}</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge className={statusClasses[step.status]} data-testid={`dvr-step-status-${step.step_key}`}>{statusLabels[step.status]}</Badge>
                <span className="text-xs font-semibold text-slate-500" data-testid={`dvr-step-percent-${step.step_key}`}>{step.completion_percentage}%</span>
              </div>
              <Progress value={step.completion_percentage} className="mt-2 h-1.5" data-testid={`dvr-step-progress-${step.step_key}`} />
            </div>
          </div>
        </button>
      ))}
    </aside>
  );
}

function StepEditor({ step, stepIndex, totalSteps, onSave, onBack, onNext, saving }) {
  const [values, setValues] = useState(step.field_values || {});

  useEffect(() => {
    setValues(step.field_values || {});
  }, [step]);

  const required = useMemo(() => {
    const combined = new Set([...(step.missing_required_fields || []), ...Object.keys(values)]);
    if (combined.size === 0 && step.status === "completo") return Object.keys(values);
    return Array.from(combined);
  }, [step, values]);

  const toggle = (label, checked) => setValues((prev) => ({ ...prev, [label]: checked }));

  return (
    <Card className="rounded-lg border-slate-200 shadow-none" data-testid="dvr-step-editor-card">
      <CardHeader className="border-b border-slate-100">
        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900" data-testid="dvr-step-editor-kicker">Step {stepIndex + 1} di {totalSteps}</p>
            <CardTitle className="mt-2 font-heading text-3xl text-slate-950" data-testid="dvr-step-editor-title">{step.step_title}</CardTitle>
          </div>
          <Badge className={statusClasses[step.status]} data-testid="dvr-step-editor-status">{statusLabels[step.status]} · {step.completion_percentage}%</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 p-6">
        {step.warnings?.length > 0 ? (
          <Alert className="border-amber-200 bg-amber-50 text-amber-900" data-testid="dvr-step-warning-alert">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Controlli richiesti</AlertTitle>
            <AlertDescription data-testid="dvr-step-warning-text">{step.warnings.join(" · ")}</AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800" data-testid="dvr-step-ok-alert">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Step completo</AlertTitle>
            <AlertDescription>Tutti i controlli obbligatori risultano soddisfatti.</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-3" data-testid="dvr-step-required-checks">
          <p className="text-sm font-semibold text-slate-700" data-testid="dvr-step-required-title">Campi obbligatori e controlli</p>
          {required.length === 0 && <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-500" data-testid="dvr-step-no-manual-fields">Questo step è calcolato automaticamente dai dati già presenti nell’azienda.</p>}
          {required.map((label) => (
            <label key={label} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid={`dvr-step-field-${label.toLowerCase().replaceAll(" ", "-")}`}>
              <span className="text-sm font-medium text-slate-700">{label}</span>
              <input type="checkbox" checked={Boolean(values[label]) || !step.missing_required_fields.includes(label)} onChange={(event) => toggle(label, event.target.checked)} data-testid={`dvr-step-checkbox-${label.toLowerCase().replaceAll(" ", "-")}`} />
            </label>
          ))}
        </div>

        <div className="flex flex-col-reverse justify-between gap-3 border-t border-slate-100 pt-5 sm:flex-row" data-testid="dvr-step-actions">
          <Button variant="outline" onClick={onBack} disabled={stepIndex === 0 || saving} data-testid="dvr-step-back-button"><ArrowLeft className="h-4 w-4" /> Torna indietro</Button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => onSave(values, false)} disabled={saving} data-testid="dvr-step-save-button"><Save className="h-4 w-4" /> Salva</Button>
            <Button className="bg-blue-900 hover:bg-blue-800" onClick={() => onSave(values, true)} disabled={saving} data-testid="dvr-step-save-continue-button">Salva e continua <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <button className="sr-only" onClick={onNext} data-testid="dvr-step-hidden-next-button">Avanti</button>
      </CardContent>
    </Card>
  );
}

export default function DvrWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wizard, setWizard] = useState(null);
  const [activeKey, setActiveKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    apiRequest(`/companies/${id}/dvr-wizard`).then((data) => {
      setWizard(data);
      setActiveKey(data.steps[0]?.step_key);
    }).catch((err) => setError(err.message));
  }, [id]);

  if (!id) return <CompanyPicker />;
  if (!wizard) return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-wizard-loading">Caricamento DVR Wizard...</div>;

  const activeIndex = Math.max(0, wizard.steps.findIndex((step) => step.step_key === activeKey));
  const activeStep = wizard.steps[activeIndex] || wizard.steps[0];

  const saveStep = async (values, continueNext) => {
    setSaving(true);
    setError("");
    try {
      const data = await apiRequest(`/companies/${id}/dvr-wizard/steps/${activeStep.step_key}`, {
        method: "PUT",
        body: JSON.stringify({ field_values: values }),
      });
      setWizard(data);
      if (continueNext && activeIndex < data.steps.length - 1) setActiveKey(data.steps[activeIndex + 1].step_key);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dvr-wizard-page">
      <div className="flex flex-wrap items-center justify-between gap-3" data-testid="dvr-wizard-top-actions">
        <Button variant="outline" onClick={() => navigate(`/aziende/${id}`)} data-testid="dvr-wizard-back-company-button"><ArrowLeft className="h-4 w-4" /> Dettaglio azienda</Button>
        <Button asChild variant="outline" data-testid="dvr-wizard-companies-button"><Link to="/aziende"><Building2 className="h-4 w-4" /> Elenco aziende</Link></Button>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-wizard-header">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900" data-testid="dvr-wizard-kicker">Assessment DVR v{wizard.assessment.version_number}</p>
            <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950" data-testid="dvr-wizard-title">{wizard.company.business_name}</h2>
            <p className="mt-2 text-slate-600" data-testid="dvr-wizard-meta">Riferimento normativo: {wizard.assessment.legal_reference_version} · Stato {wizard.assessment.status}</p>
          </div>
          <div className="w-full max-w-md" data-testid="dvr-wizard-overall-panel">
            <div className="mb-2 flex justify-between text-sm"><span>Percentuale generale DVR</span><strong data-testid="dvr-wizard-overall-value">{wizard.overall_completion_percentage}%</strong></div>
            <Progress value={wizard.overall_completion_percentage} className="h-3" data-testid="dvr-wizard-overall-progress" />
          </div>
        </div>
      </section>

      {error && <Alert variant="destructive" data-testid="dvr-wizard-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      <section className="grid gap-6 xl:grid-cols-[360px_1fr]" data-testid="dvr-wizard-layout">
        <StepChecklist steps={wizard.steps} activeKey={activeStep.step_key} setActiveKey={setActiveKey} />
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3" data-testid="dvr-wizard-summary-cards">
            <Card className="rounded-lg shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Step completi</p><p className="font-heading text-2xl font-bold" data-testid="dvr-wizard-complete-count">{wizard.steps.filter((step) => step.status === "completo").length}</p></CardContent></Card>
            <Card className="rounded-lg shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Da completare</p><p className="font-heading text-2xl font-bold" data-testid="dvr-wizard-missing-count">{wizard.steps.filter((step) => step.status !== "completo").length}</p></CardContent></Card>
            <Card className="rounded-lg shadow-none"><CardContent className="p-4"><p className="text-sm text-slate-500">Anteprima</p><p className="font-heading text-2xl font-bold" data-testid="dvr-wizard-preview-state"><FileText className="inline h-6 w-6 text-blue-900" /> DVR</p></CardContent></Card>
          </div>
          <StepEditor
            step={activeStep}
            stepIndex={activeIndex}
            totalSteps={wizard.steps.length}
            saving={saving}
            onBack={() => activeIndex > 0 && setActiveKey(wizard.steps[activeIndex - 1].step_key)}
            onNext={() => activeIndex < wizard.steps.length - 1 && setActiveKey(wizard.steps[activeIndex + 1].step_key)}
            onSave={saveStep}
          />
          <Card className="rounded-lg border-slate-200 bg-slate-950 text-white shadow-none" data-testid="dvr-wizard-preview-card">
            <CardContent className="flex flex-col justify-between gap-4 p-5 sm:flex-row sm:items-center">
              <div><p className="font-heading text-xl font-semibold" data-testid="dvr-wizard-preview-title"><ClipboardCheck className="mr-2 inline h-5 w-5" /> Anteprima DVR</p><p className="mt-1 text-sm text-slate-300" data-testid="dvr-wizard-preview-text">L’anteprima diventa pronta quando tutti gli step risultano completi.</p></div>
              <Badge className={wizard.overall_completion_percentage === 100 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} data-testid="dvr-wizard-preview-badge">{wizard.overall_completion_percentage === 100 ? "Pronta" : "In preparazione"}</Badge>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}